import Header from "../components/Header";
import Empty from "../components/Empty";
import Error from "../components/Error";
import Meta from "../../../components/Meta";
import Data from "../../../../repository";
import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";

const formatBalance = (value) => Number(value || 0);

const PAGE_SIZE = 20;

const Users = () => {
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(false);
    const [importResult, setImportResult] = useState(null);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 300);
        return () => clearTimeout(timeout);
    }, [search]);

    const refreshUsers = async () => {
        try {
            setLoading(true);
            const result = await Data.fetchUsersPage({ page, pageSize: PAGE_SIZE, search: debouncedSearch });
            setUsers(result.data.data);
            setTotalPages(result.data.totalPages);
        } catch (error) {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(async () => {
        await refreshUsers();
    }, [debouncedSearch, page]);

    const downloadBlob = (response, filename) => {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const exportCsv = async () => {
        try {
            setExporting(true);
            setExportError(false);

            const response = await Data.exportUsers();
            downloadBlob(response, 'users.csv');
        } catch (error) {
            setExportError(true);
        } finally {
            setExporting(false);
        }
    };

    const downloadSample = async () => {
        try {
            setExportError(false);
            const response = await Data.exportUserImportTemplate();
            downloadBlob(response, 'users-import-template.csv');
        } catch (error) {
            setExportError(true);
        }
    };

    const importCsv = async () => {
        if(!importFile) return;
        try {
            setImporting(true);
            setImportError(false);
            setImportResult(null);
            const result = await Data.importUsers(importFile);
            setImportResult(result.data);
            setImportFile(null);
            await refreshUsers();
        } catch (error) {
            setImportError(true);
        } finally {
            setImporting(false);
        }
    };

    return (
        <>
            <Meta title={"Users"} />
            <Header heading={"Users"} />
            <div className={"p-3 lg:p-6 grid grid-cols-1 gap-3 lg:gap-6 w-full"}>
                <div className={"flex flex-wrap justify-between gap-3"}>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, email, or phone..."
                        className={"bg-white border rounded-md px-3 py-2 min-w-[260px]"}
                    />
                    <div className={"flex flex-wrap gap-2"}>
                        <button
                            type="button"
                            onClick={downloadSample}
                            disabled={loading}
                            className={"bg-backgroundGrey px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                        >
                            Download Sample Sheet
                        </button>
                        <button
                            type="button"
                            onClick={exportCsv}
                            disabled={loading || exporting}
                            className={"bg-primary text-white px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                        >
                            {exporting ? "Exporting..." : "Export Users"}
                        </button>
                    </div>
                </div>

                <div className={"bg-white border rounded-lg p-4 flex flex-wrap items-center gap-3"}>
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                        className={"bg-white border rounded-md px-3 py-2"}
                    />
                    <button
                        type="button"
                        onClick={importCsv}
                        disabled={!importFile || importing}
                        className={"bg-primary text-white px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                    >
                        {importing ? "Importing..." : "Import Users"}
                    </button>
                    <p className={"text-sm text-textGrey"}>
                        Use walletBalance for the current wallet balance in minor units, for example 2500 for £25.00. Imported users should use Forgot Password to set their password before logging in.
                    </p>
                </div>

                {error ? <Error message={"Unable to load users, please refresh the page."} /> : null}
                {exportError ? <Error message={"Unable to export users, please try again."} /> : null}
                {importError ? <Error message={"Unable to import users. Check the CSV and try again."} /> : null}
                {importResult ? (
                    <div className={"bg-white border rounded-lg p-4"}>
                        <p>Import complete: {importResult.created} created, {importResult.updated} updated, {importResult.walletAdjustments} wallet adjustments, {importResult.skipped} skipped.</p>
                        {importResult.errors?.length ? (
                            <ul className={"mt-2 text-sm text-red-700"}>
                                {importResult.errors.map((item, index) => <li key={index}>Row {item.row}: {item.message}</li>)}
                            </ul>
                        ) : null}
                    </div>
                ) : null}

                {loading ? (
                    <Empty>
                        <p>Loading...</p>
                    </Empty>
                ) : users.length === 0 ? (
                    <Empty>
                        <p>No Users</p>
                    </Empty>
                ) : (
                    <>
                    <div className={"w-full overflow-x-auto"} style={{ WebkitOverflowScrolling: 'touch' }}>
                    <table className={"min-w-[640px] w-full border-collapse outline outline-1 outline-gray-200 rounded-lg overflow-hidden drop-shadow-sm"}>
                        <thead className={"bg-gray-50"}>
                        <tr>
                            <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Name</th>
                            <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Email</th>
                            <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Phone</th>
                            <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Status</th>
                            <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Actions</th>
                        </tr>
                        </thead>
                        <tbody className={"bg-white"}>
                        {users.map((user) => (
                            <tr className={"border-t"} key={user._id}>
                                <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{user.firstName} {user.lastName}</td>
                                <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{user.email}</td>
                                <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{user.phoneNumber || '--'}</td>
                                <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{user.isActive ? "Active" : "Inactive"}</td>
                                <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>
                                    <NavLink to={`/admin/users/${user._id}`} className={"bg-backgroundGrey px-2.5 py-1 rounded hover:opacity-80 transition"}>View</NavLink>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>

                    <div className={"flex items-center gap-2 justify-center"}>
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className={`px-2.5 py-1.5 rounded-md border ${page <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Prev
                        </button>
                        <span className={"text-textGrey text-sm"}>Page {page} of {totalPages}</span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className={`px-2.5 py-1.5 rounded-md border ${page >= totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            Next
                        </button>
                    </div>
                    </>
                )}
            </div>
        </>
    );
};

export default Users;
